import { AdhesionUnit, AdhesionUnitObserverCallback, AdhesionUnitSubscriberCallback, AdhesionUnitWatcherInterface } from '@mbrtargeting/metatag-shared-types';
import { findFirstMatchingQuery, getViewportDimensions, isHTMLElement, isRectangleVisible, isSelectorValid, requestAnimationFrameThrottled, VisibilityMode } from '@mbrtargeting/metatag-utils';

export interface AdhesionUnitWatcherOptions {
    adhesionUnitQueries: string[],
    adhesionUnitGap: number,
}

interface ObservedElement {
    target: HTMLElement;
    callback: AdhesionUnitObserverCallback;
    isIntersecting: boolean;
    height: number;
}

const isSameTarget = (a: { target: Node }) => (b: { target: Node }) => a.target === b.target;

export class AdhesionUnitWatcher implements AdhesionUnitWatcherInterface {

    /**
     * the AdhesionUnit metadata which is updated by this watcher
     */
    private adhesionUnit: AdhesionUnit = {
        isActive: false,
        node: null,
        height: 0,
        gap: 0,
        queries: [],
        isVisible: false,
        selector: undefined, //can possibly be removed. Would WebDev still need this? No occurrence in templates-ts
    };
    private callbacks: AdhesionUnitSubscriberCallback[] = [];
    private intersectionObserver: IntersectionObserver | undefined;
    private targetElements: ObservedElement[] = [];

    constructor() { }

    init(adhesionUnitOptions: AdhesionUnitWatcherOptions) {
        const { adhesionUnitQueries: queries = [], adhesionUnitGap = 0 } = adhesionUnitOptions;

        //construct meta data first time
        this.adhesionUnit = {
            ...this.adhesionUnit,
            isActive: queries.length !== 0,
            queries,
            gap: +adhesionUnitGap,
        };

        // periodically update the metadata, so timed changes from publishers can be caught (ex: after 5sec, collapse sticky nav)
        window.setInterval(() => this.#updateAdhesionUnit(), 500);

        // update the metadata more often when scrolling, but throttled. Catches changes to adhesion unit while user scrolls.
        window.addEventListener('scroll', () => this.#updateAdhesionUnit());

        // initially update the metadata
        this.#updateAdhesionUnit();
    }

    /**
     * Creates IntersectionObserver to watch any elements passed via observe function and will send changes to their callback.
     *
     * To extrapolate a working IntersectionObserver, we need left and right values from adhesionUnits DOMRect.
     * Observer will use viewportHeight * 2 to search for collisions, so elements will have ample time to react.
     */
    private createIntersectionObserver(nodeDomRect: DOMRectReadOnly | undefined) {
        const { viewportWidth, viewportHeight } = getViewportDimensions();
        const { left = 0, right = 0 } = nodeDomRect || {}; //possible simplification by only passing left and rights to the function?

        //update isIntersecting state on observedElement when state changes.
        const handleObserverStateChanges = (entries: IntersectionObserverEntry[]) => {
            for (const { isIntersecting, target } of entries) {
                const elements = this.targetElements;
                const index = elements.findIndex(isSameTarget({ target }));
                if (index < 0) return;

                elements[index] = this.updateObservedElement(elements[index], {
                    isIntersecting
                });
            }
        };

        const observer = new IntersectionObserver(handleObserverStateChanges, {
            rootMargin: `0px ${-(viewportWidth - right)}px ${viewportHeight}px ${-left}px` //honor left and right, but inflate height so sticky elements are colliding very early
        });

        this.targetElements.forEach(({ target }) => observer.observe(target));
        return observer;
    }

    /**
     * compare old ObservedElement values against new states. If it has changed, set the new values and trigger callback.
     * Based on sending "isIntersecting" and "height", callback will trigger external logic and decides if and how adhesionUnit should be avoided.
     */
    updateObservedElement(oldState: ObservedElement, newState: Partial<ObservedElement>): ObservedElement {
        const { isIntersecting: newIsIntersecting, height: newHeight } = newState;
        const { isIntersecting: wasIsIntersecting, height: oldHeight, callback } = oldState;

        const isIntersecting = newIsIntersecting ?? wasIsIntersecting ?? false;
        const height = (!isIntersecting) ? 0 : newHeight ?? oldHeight ?? 0;
        const changed = wasIsIntersecting !== isIntersecting || oldHeight !== height;

        if (changed) callback({
            isIntersecting,
            height,
        });

        return {
            ...oldState,
            isIntersecting,
            height,
        };
    }

    /**
     * Will check if the site has an adhesion unit configured (some element that will scroll with the user and might occlude ads that are also scrolling along the user).
     * This might be some form of sticky navigation or some sticky user information panel.
     * Invoking this method is very costly performance wise, since it will employ offset values and clientRects() against the adhesion unit.
     * Only use this sparingly or with some kind of interval, and not every frame.
     */
    #updateAdhesionUnit = requestAnimationFrameThrottled(() => {
        const { gap, height: oldHeight, isVisible: wasVisible, queries } = this.adhesionUnit;
        const { node, selector } = findFirstMatchingQuery(queries);
        const rect: DOMRectReadOnly | undefined = isHTMLElement(node) ? node.getBoundingClientRect() : undefined;

        if (!rect) return; //most likely element valid in DOM, but not reachable / rendered;

        const isVisible = isRectangleVisible(rect, VisibilityMode.PARTIAL);
        const height = isVisible ? rect.bottom + gap : 0;

        this.adhesionUnit = {
            ...this.adhesionUnit,
            node,
            selector,
            isVisible,
            height,
        };

        //update internal observed elements
        this.targetElements = this.targetElements.map((target) => this.updateObservedElement(target, { height }));
        //create intersectionObserver when adhesionUnit is first found and visible
        if (isVisible && !this.intersectionObserver) this.intersectionObserver = this.createIntersectionObserver(rect);

        // trigger callback functions if adhesionUnit height or visibility was actually changed
        if (height !== oldHeight || isVisible !== wasVisible) {
            for (const callback of this.callbacks) {
                try {
                    callback(this.adhesionUnit);
                } catch (e) {
                    console.log('AdhesionUnitWatcher: callback error %o', [e]);
                    this.unsubscribe(callback);
                }
            }
        }
    });

    /**
     * returns the saved values for the adhesion unit, which are update only when the user scrolls and the last update was at least 250ms ago.
     *
     * @returns AdhesionUnit metadata
     */
    public getAdhesionUnit(): AdhesionUnit {
        return this.adhesionUnit;
    }

    /**
     * Changes the current adhesionUnit.
     *
     * This is used for use cases, where templates or formats need to either dynamically change the adhesion unit, or the publisher changes the site significantly (single page applications).
     *
     * @param selector query selector matching the new adhesion unit. Selector will not be checked for plausibility, make sure to only pass a valid selector.
     */
    public changeAdhesionUnit(selector: string): void {
        // if input from template or publisher is not usable, do not update the selector
        if (!isSelectorValid(selector)) return;

        // update the internal metadata
        this.adhesionUnit = {
            ...this.adhesionUnit,
            isActive: true,
            node: null,
            height: 0,
            isVisible: false,
            queries: [selector],
            selector,
        };
        //re-create observer, need left and right pos from element. If not available yet, skip and wait for adhesionUnit to be found during regular updates.
        const adhesionUnit = document.querySelector(selector);
        if (!adhesionUnit) return;

        const adhesionUnitRect = adhesionUnit.getBoundingClientRect();
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = this.createIntersectionObserver(adhesionUnitRect);
        // immediately trigger an evaluation
        this.#updateAdhesionUnit();
    }

    public subscribe(callback: AdhesionUnitSubscriberCallback): void {
        this.callbacks.push(callback);
        callback(this.adhesionUnit); //initial run of callback with current adhesionUnit value
    }

    public unsubscribe(callback: AdhesionUnitSubscriberCallback): void {
        this.callbacks = this.callbacks.filter((arrayEntry) => arrayEntry !== callback);
    }

    /**
     * observe if passed HTMLElement will collide with the adhesion unit.
     * Callback will be triggered when adhesionUnit is changed or element starts/stop intersecting with the unit
     * ToDo: allow multiple callbacks for one element
     *
     * Currently named to avoid collision with already know subscribe / unsubscribe. Potentially switch both properties to this implementation later on
     *
     * @param target
     * @param callback
     */
    public observe(target: HTMLElement, callback: AdhesionUnitObserverCallback): void {
        if (!isHTMLElement(target)) return;
        if (!this.targetElements.find(isSameTarget({ target }))) {
            this.targetElements.push({
                target,
                callback,
                isIntersecting: false,
                height: this.adhesionUnit.height
            });
            this.intersectionObserver?.observe(target);
        }
    }

    /**
     * remove passed HTMLElement from observer. Callback will not be triggered anymore
     *
     * Currently named to avoid collision with already know subscribe / unsubscribe. Potentially switch both properties to this implementation later on
     * @param target
     */
    public unobserve(target: HTMLElement): void {
        if (!isHTMLElement(target)) return;

        const index = this.targetElements.findIndex(isSameTarget({ target }));
        if (index >= 0) this.targetElements.splice(index, 1);
        this.intersectionObserver?.unobserve(target);
    }
}
